import { useEffect, useMemo, useState } from 'react';
import { useForm, type FieldValues } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Loader2 } from 'lucide-react';
import { getMobileConfig } from '@/features/auth/utils/mobileNumber';
import {
  useTeamCreateFieldsQuery,
  useTeamMemberQuery,
  useUpdateTeamMemberMutation,
} from '../api/useTeamQueries';
import { DynamicProfileFields } from '../components/DynamicProfileFields';
import {
  buildTeamFormDefaults,
  buildTeamUpdatePayload,
  extractTeamMemberData,
  normalizeTeamCreateFields,
} from '../utils/teamUtils';

export default function EditTeamMemberPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const mobileConfig = useMemo(() => getMobileConfig(), []);
  const [hydrated, setHydrated] = useState(false);

  const createFieldsQ = useTeamCreateFieldsQuery();
  const memberQ = useTeamMemberQuery(id);
  const updateMut = useUpdateTeamMemberMutation();

  const {
    register,
    control,
    handleSubmit,
    reset,
    setError,
    clearErrors,
    formState: { errors, isValid },
  } = useForm<FieldValues>({ mode: 'onChange' });

  const selectedFields = useMemo(
    () => normalizeTeamCreateFields(createFieldsQ.data),
    [createFieldsQ.data],
  );

  const memberData = useMemo(
    () => extractTeamMemberData(memberQ.data),
    [memberQ.data],
  );

  useEffect(() => {
    if (hydrated) return;
    if (!selectedFields.length || !Object.keys(memberData).length) return;

    reset(buildTeamFormDefaults(selectedFields, memberData));
    setHydrated(true);
  }, [selectedFields, memberData, hydrated, reset]);

  const isLoading =
    memberQ.isPending || createFieldsQ.isPending || !hydrated;

  const onSubmit = (data: FieldValues) => {
    if (!id) return;

    const disableUser =
      memberData.disableUser === true || memberData.isDisabled === true;

    updateMut.mutate(
      {
        memberId: id,
        payload: buildTeamUpdatePayload(data, {
          mobileCountryCode: mobileConfig.code,
          disableUser,
        }),
      },
      { onSuccess: () => navigate(`/team/${id}`) },
    );
  };

  if (memberQ.isError) {
    return (
      <div className="mx-auto flex w-full max-w-3xl flex-col gap-4 py-10 text-left">
        <p className="m-0 text-sm text-[var(--dash-muted)]">Team member not found.</p>
        <button
          type="button"
          onClick={() => navigate('/team')}
          className="inline-flex w-fit items-center gap-1.5 text-sm font-medium text-[var(--dash-brand)]"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to team
        </button>
      </div>
    );
  }

  return (
    <div className="flex w-full flex-col gap-3">
      <button
        type="button"
        onClick={() => navigate(id ? `/team/${id}` : '/team')}
        className="inline-flex w-fit items-center gap-1.5 text-sm font-medium text-[var(--dash-muted)] transition hover:text-[var(--dash-ink)]"
      >
        <ArrowLeft className="h-4 w-4" />
        Back
      </button>

      <header className="camp-fade-up text-left">
        <p className="m-0 text-[11px] font-semibold tracking-[0.12em] text-[var(--dash-brand)] uppercase">
          Team
        </p>
        <h1 className="m-0 mt-0.5 text-xl font-semibold tracking-tight text-[var(--dash-ink)] sm:text-2xl">
          Edit Team Member
        </h1>
        <p className="mt-0.5 mb-0 text-sm text-[var(--dash-muted)]">
          Update profile details from the member record. Email can&apos;t be changed here.
        </p>
      </header>

      {isLoading ? (
        <div className="flex justify-center py-24">
          <Loader2 className="h-6 w-6 animate-spin text-[var(--dash-brand)]" />
        </div>
      ) : createFieldsQ.isError ? (
        <p className="m-0 text-sm text-red-600">
          Could not load profile fields. Please try again.
        </p>
      ) : (
        <form
          onSubmit={handleSubmit(onSubmit)}
          className="camp-fade-up flex flex-col gap-3"
          style={{ animationDelay: '60ms' }}
        >
          <section className="camp-section">
            <header className="mb-5 border-b border-[var(--dash-line)] pb-4 text-left">
              <h2 className="m-0 text-base font-semibold tracking-tight text-[var(--dash-ink)]">
                Profile Identity
              </h2>
              <p className="m-0 mt-0.5 text-sm text-[var(--dash-muted)]">
                Email can&apos;t be changed here.
              </p>
            </header>

            {selectedFields.length === 0 ? (
              <p className="m-0 text-sm text-[var(--dash-muted)]">
                No profile fields are configured for team members.
              </p>
            ) : (
              <DynamicProfileFields
                fields={selectedFields}
                register={register}
                control={control}
                errors={errors}
                setError={setError}
                clearErrors={clearErrors}
                mobileConfig={mobileConfig}
                readOnlyFields={['email']}
              />
            )}
          </section>

          <div className="sticky bottom-3 z-10 flex flex-col-reverse gap-3 rounded-2xl border border-[var(--dash-line)] bg-white/95 p-3 shadow-[0_12px_32px_-18px_rgba(16,38,73,0.45)] backdrop-blur sm:flex-row sm:justify-end">
            <button
              type="button"
              onClick={() => navigate(id ? `/team/${id}` : '/team')}
              className="h-11 rounded-xl px-5 text-sm font-semibold text-[var(--dash-muted)] transition hover:bg-[var(--dash-soft)] hover:text-[var(--dash-ink)]"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={!isValid || updateMut.isPending || selectedFields.length === 0}
              className="dash-btn-gradient inline-flex h-11 items-center justify-center gap-2 rounded-xl px-6 text-sm font-semibold text-white disabled:opacity-50"
            >
              {updateMut.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
              Save Changes
            </button>
          </div>
        </form>
      )}
    </div>
  );
}
